/**
 * Vendor Documents Management
 * Upload, verify, and track W-9 forms, COI (Certificate of Insurance), and licenses
 * Includes OCR extraction and expiry tracking
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { getEnv } from '@/lib/env';

const { supabaseUrl, supabaseAnonKey } = getEnv();

interface VendorDocument {
  id: string;
  vendor_id: string;
  document_type: 'w9' | 'coi' | 'license' | 'other';
  file_url: string;
  file_name: string;
  upload_date: string;
  expiry_date?: string;
  verified: boolean;
  extracted_data?: any;
  status: 'pending' | 'verified' | 'expired' | 'rejected';
}

interface VendorDocumentsProps {
  vendorId: string;
  vendorName: string;
}

export default function VendorDocuments({ vendorId, vendorName }: VendorDocumentsProps) {
  const [documents, setDocuments] = useState<VendorDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDocuments();
  }, [vendorId]);

  const loadDocuments = async () => {
    setLoading(true);
    
    const { data } = await supabase
      .from('vendor_documents')
      .select('*')
      .eq('vendor_id', vendorId)
      .order('upload_date', { ascending: false });
    
    setDocuments(data || []);
    setLoading(false);
  };

  const handleFileUpload = async (file: File, documentType: string) => {
    setUploading(true);

    try {
      // Upload file to Supabase Storage
      const fileName = `${vendorId}/${documentType}_${Date.now()}_${file.name}`;
      
      const { data: uploadData, error: uploadError } = await supabase
        .storage
        .from('vendor-documents')
        .upload(fileName, file, {
          contentType: file.type,
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase
        .storage
        .from('vendor-documents')
        .getPublicUrl(fileName);

      // Extract data using OCR (if PDF/image)
      const extractedData = await extractDocumentData(publicUrl, documentType);

      // Create document record
      const { data: doc, error: dbError } = await supabase
        .from('vendor_documents')
        .insert({
          vendor_id: vendorId,
          document_type: documentType,
          file_url: publicUrl,
          file_name: file.name,
          upload_date: new Date().toISOString(),
          expiry_date: extractedData?.expiry_date,
          extracted_data: extractedData,
          status: 'pending',
          verified: false
        })
        .select()
        .single();

      if (dbError) throw dbError;

      await loadDocuments();
      alert('Document uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const extractDocumentData = async (fileUrl: string, docType: string): Promise<any> => {
    // In production, this would call an OCR service like:
    // - AWS Textract
    // - Google Document AI
    // - Azure Form Recognizer
    
    // For now, return mock extracted data
    return {
      extracted: true,
      expiry_date: docType === 'coi' || docType === 'license' 
        ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        : null,
      confidence: 0.95
    };
  };

  const handleVerify = async (documentId: string) => {
    await supabase
      .from('vendor_documents')
      .update({ 
        verified: true, 
        status: 'verified',
        verified_at: new Date().toISOString()
      })
      .eq('id', documentId);
    
    await loadDocuments();
  };

  const handleReject = async (documentId: string) => {
    await supabase
      .from('vendor_documents')
      .update({ status: 'rejected' })
      .eq('id', documentId);
    
    await loadDocuments();
  };

  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'w9': return '📋';
      case 'coi': return '🛡️';
      case 'license': return '📜';
      default: return '📄';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return 'bg-green-100 text-green-800';
      case 'expired': return 'bg-red-100 text-red-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Vendor Documents</h2>
          <p className="text-gray-600">{vendorName}</p>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="font-semibold text-gray-900 mb-4">Upload Documents</h3>
        <div className="grid grid-cols-3 gap-4">
          {['w9', 'coi', 'license'].map((type) => (
            <div key={type} className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors">
              <div className="text-4xl mb-2">{getDocumentIcon(type)}</div>
              <h4 className="font-medium text-gray-900 mb-2">
                {type === 'w9' ? 'W-9 Form' : type === 'coi' ? 'Insurance COI' : 'License'}
              </h4>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], type)}
                className="hidden"
                id={`upload-${type}`}
              />
              <label
                htmlFor={`upload-${type}`}
                className="cursor-pointer inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Upload
              </label>
            </div>
          ))}
        </div>
        {uploading && (
          <div className="mt-4 text-center text-blue-600">
            Uploading and processing...
          </div>
        )}
      </div>

      {/* Documents List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h3 className="font-semibold text-gray-900">Uploaded Documents</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : documents.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            No documents uploaded yet
          </div>
        ) : (
          <div className="divide-y">
            {documents.map((doc) => (
              <div key={doc.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="text-3xl">{getDocumentIcon(doc.document_type)}</div>
                    <div>
                      <div className="font-medium text-gray-900">{doc.file_name}</div>
                      <div className="text-sm text-gray-600">
                        {doc.document_type.toUpperCase()} • Uploaded {new Date(doc.upload_date).toLocaleDateString()}
                      </div>
                      {doc.expiry_date && (
                        <div className="text-sm text-gray-600">
                          Expires: {new Date(doc.expiry_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(doc.status)}`}>
                      {doc.status}
                    </span>
                    <a
                      href={doc.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm"
                    >
                      View
                    </a>
                    {doc.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleVerify(doc.id)}
                          className="px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-sm"
                        >
                          Verify
                        </button>
                        <button
                          onClick={() => handleReject(doc.id)}
                          className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm"
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
